import cn from "classnames";
import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  RefCallback,
  useCallback,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import { Portal } from "../common/components/portal";
import { useLatestRef, useRequestAnimationFrame } from "../common/hooks";
import { useMounted } from "../common/utils/use-mounted";
import {
  computeCoordinatesFromPlacement,
  correctCoordinatesByPaddings,
  deepEqual,
  getAvailablePosition,
  getElementAbsoluteRects,
} from "./utils";
import { DEFAULT_POSITIONS } from "./constants";
import { FlyoutProps, Position } from "./types";
import { getOverflowAncestors } from "./get-overflow-ancestors";
import styles from "./flyout.module.scss";

// Компонент требует высоту document.body равной 100% от высоты вьюпорта
export const Flyout = ({
  id,
  children,
  parentRef,
  className,
  positionsOrder: positionsOrderFromProps = DEFAULT_POSITIONS,
  onChangePosition,
  dataTestId = "flyout",
  shouldUseParentWidth,
  width,
  ...restProps
}: FlyoutProps) => {
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
  const [flyoutCoordinates, setFlyoutCoordinates] = useState({ x: 0, y: 0 });
  const [positionsOrder, setPositionsOrder] = useState(positionsOrderFromProps);

  const flyoutRef = useRef<HTMLDivElement | null>(null);

  const previousOnChangePosition = useLatestRef(onChangePosition);

  useEffect(() => {
    if (currentPosition === null) {
      return;
    }

    previousOnChangePosition.current?.(currentPosition);
  }, [currentPosition, previousOnChangePosition]);

  const overflowAncestors = useMemo(
    () =>
      parentRef.current !== null
        ? getOverflowAncestors(parentRef.current)
        : null,
    [parentRef]
  );

  useEffect(() => {
    if (!deepEqual(positionsOrder, positionsOrderFromProps)) {
      setPositionsOrder(positionsOrderFromProps);
    }
  }, [positionsOrder, positionsOrderFromProps]);

  const isMounted = useMounted();

  const updateFlyoutPosition = useCallback(() => {
    if (!isMounted() || !flyoutRef.current || !parentRef.current) {
      return;
    }

    const parentRect = getElementAbsoluteRects(parentRef.current);

    let newFlyoutWidth: string | undefined = undefined;

    if (typeof width === "number") {
      newFlyoutWidth = `${width}px`;
    } else if (shouldUseParentWidth) {
      newFlyoutWidth = `${parentRect.width}px`;
    }

    if (newFlyoutWidth !== undefined) {
      // необходимо установить ширину до её считывания из браузера для предотвращения первоначального появления флайаута в некорректной позиции
      flyoutRef.current.style.setProperty("width", newFlyoutWidth);
    }

    const flyoutRect = getElementAbsoluteRects(flyoutRef.current);
    const containerRect = getElementAbsoluteRects(document.body);

    // выбираем доступную позицию в соответствии с приоритетом пользователя либо первую из указанных пользователем
    const availablePosition = getAvailablePosition(positionsOrder, {
      flyoutRect,
      parentRect,
      containerRect,
    });

    const { x, y } = computeCoordinatesFromPlacement(
      {
        flyout: flyoutRect,
        parent: parentRect,
      },
      availablePosition
    );

    const { paddingTop, paddingBottom, paddingLeft, paddingRight } =
      getComputedStyle(parentRef.current);

    const { x: correctedX, y: correctedY } = correctCoordinatesByPaddings({
      paddingBottom,
      paddingLeft,
      paddingRight,
      paddingTop,
      position: availablePosition,
      x,
      y,
    });

    if (
      !deepEqual(correctedX, flyoutCoordinates.x) ||
      !deepEqual(correctedY, flyoutCoordinates.y)
    ) {
      flushSync(() => {
        setFlyoutCoordinates({
          x: correctedX,
          y: correctedY,
        });
      });
    }

    // необходимо обновить параметры моментально, без ожидания следующего ререндера,
    // чтобы предотвратить появление скролла до момента расчёта корректной позиции
    flushSync(() => {
      setCurrentPosition(availablePosition);
    });
  }, [
    isMounted,
    parentRef,
    width,
    shouldUseParentWidth,
    positionsOrder,
    flyoutCoordinates.x,
    flyoutCoordinates.y,
  ]);

  const recalculatePositionOnNextFrame =
    useRequestAnimationFrame(updateFlyoutPosition);

  const flyoutRefCallback: RefCallback<HTMLDivElement> = (node) => {
    flyoutRef.current = node;
    recalculatePositionOnNextFrame();
  };

  useLayoutEffect(() => {
    if (overflowAncestors) {
      overflowAncestors.forEach((scrollableAncestor) => {
        scrollableAncestor.addEventListener(
          "scroll",
          recalculatePositionOnNextFrame
        );
      });
    }

    window.addEventListener("scroll", recalculatePositionOnNextFrame);
    window.addEventListener("resize", recalculatePositionOnNextFrame);

    return () => {
      window.removeEventListener("scroll", recalculatePositionOnNextFrame);
      window.removeEventListener("resize", recalculatePositionOnNextFrame);

      if (overflowAncestors) {
        overflowAncestors.forEach((scrollableAncestor) => {
          scrollableAncestor.removeEventListener(
            "scroll",
            recalculatePositionOnNextFrame
          );
        });
      }
    };
  }, [recalculatePositionOnNextFrame, overflowAncestors]);

  const isFlyoutVisible = isMounted() && currentPosition !== null;

  const flyoutClassNames = cn(
    styles.flyout,
    {
      [styles.visibled]: isFlyoutVisible,
    },
    className
  );

  return (
    <Portal>
      <div
        style={{ top: flyoutCoordinates.y, left: flyoutCoordinates.x }}
        className={styles.container}
        ref={flyoutRefCallback}
      >
        <div
          {...restProps}
          id={id}
          className={flyoutClassNames}
          data-test-id={dataTestId}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
};
